<template>
  <div class="explorer-overlay" @click.self="closeModal">
    <div class="explorer-shell" role="dialog" aria-modal="true" :aria-label="selectedId ? (selectedWorkspace && selectedWorkspace.name) || 'Workspace' : 'Mes workspaces'">
      <button class="close-icon" @click="closeModal" title="Fermer" aria-label="Fermer">×</button>

      <!-- ============================================================ -->
      <!-- ========================= LISTE ============================ -->
      <!-- ============================================================ -->
      <template v-if="!selectedId">
        <header class="exp-header">
          <h2 class="exp-title">Mes workspaces</h2>
          <div class="dossier-search-container">
            <svg class="search-icon-grey" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
              <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0"/>
            </svg>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Rechercher un workspace…"
              class="dossier-search-input"
            />
          </div>
        </header>

        <div class="ws-finder">
          <div v-if="loading" class="state-msg">Chargement…</div>
          <div v-else-if="items.length === 0" class="empty-state">
            <h3>Aucun workspace pour le moment</h3>
            <p>Crée un workspace depuis le bouton « + Nouveau workspace » dans la barre de recherche.</p>
          </div>
          <div v-else-if="filteredItems.length === 0" class="state-msg">
            Aucun workspace ne correspond à « {{ searchQuery }} ».
          </div>

          <div v-else class="ws-grid">
          <button
            v-for="ws in filteredItems"
            :key="ws.id"
            type="button"
            class="ws-card"
            :title="ws.status === 'ready' ? 'Rechercher dans ce workspace' : 'Voir la progression'"
            @click="searchFromCard(ws)"
          >
            <div class="ws-card-header">
              <div class="ws-card-header-left">
                <span class="ws-date-chip">{{ formatDate(ws.created_at) }}</span>
                <span
                  v-if="['queued', 'ingesting'].includes(ws.status)"
                  class="ws-mini-spinner"
                  title="Indexation en cours…"
                ></span>
                <span
                  v-else-if="ws.status === 'failed'"
                  class="ws-fail-dot"
                  :title="ws.ingest_error || 'Échec de l\'indexation'"
                ></span>
              </div>
              <div class="ws-card-actions">
                <span
                  class="ws-action-btn"
                  :class="{ 'ws-action-btn--disabled': ws.status !== 'ready' }"
                  title="Réorganiser avec l'IA"
                  @click.stop="ws.status === 'ready' && (reorgTarget = ws)"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M9.5 2.672a.5.5 0 1 0 1 0V.843a.5.5 0 0 0-1 0v1.829Zm4.5.035A.5.5 0 0 0 13.293 2L12 3.293a.5.5 0 1 0 .707.707L14 2.707ZM7.293 4A.5.5 0 1 0 8 3.293L6.707 2A.5.5 0 0 0 6 2.707L7.293 4Zm-.621 2.5a.5.5 0 1 0 0-1H4.843a.5.5 0 1 0 0 1h1.829Zm8.485 0a.5.5 0 1 0 0-1h-1.829a.5.5 0 0 0 0 1h1.829ZM13.293 10A.5.5 0 1 0 14 9.293L12.707 8a.5.5 0 1 0-.707.707L13.293 10ZM9.5 11.157a.5.5 0 0 0 1 0V9.328a.5.5 0 0 0-1 0v1.829Zm1.854-5.097a.5.5 0 0 0 0-.706l-.708-.708a.5.5 0 0 0-.707 0L8.646 5.94a.5.5 0 0 0 0 .707l.708.708a.5.5 0 0 0 .707 0l1.293-1.293Zm-3 3a.5.5 0 0 0 0-.706l-.708-.708a.5.5 0 0 0-.707 0L.646 13.94a.5.5 0 0 0 0 .707l.708.708a.5.5 0 0 0 .707 0L8.354 9.06Z"/>
                  </svg>
                </span>
                <span
                  class="ws-action-btn"
                  title="Télécharger (.zip)"
                  @click.stop="downloadArchiveFor(ws)"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M8 2v8M4.5 6.5L8 10l3.5-3.5M2.5 13h11" />
                  </svg>
                </span>
                <span
                  class="ws-action-btn ws-action-btn--danger"
                  title="Supprimer"
                  @click.stop="deleteWorkspaceFromList(ws)"
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M3 4h10M5 4V2h6v2M5 4v10h6V4M7 7v5M9 7v5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
                </span>
              </div>
            </div>

            <div class="ws-card-body">
              <span
                class="ws-tree-chevron"
                :class="{ 'ws-tree-chevron--open': cardTreeOpen.includes(ws.id) }"
                title="Déplier les fichiers pour restreindre la recherche"
                @click.stop="toggleCardTree(ws)"
              >
                <svg width="11" height="11" viewBox="0 0 16 16" fill="none"><path d="M6 3l5 5-5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </span>
              <span class="ws-card-name" :title="ws.name">{{ ws.name || 'Sans nom' }}</span>
            </div>

            <div v-if="cardTreeOpen.includes(ws.id)" class="ws-card-tree" @click.stop>
              <div v-if="cardTreeLoading.includes(ws.id)" class="ws-tree-loading">Chargement…</div>
              <div v-else-if="!cardTreeRows(ws.id).length" class="ws-tree-loading">Aucun document.</div>
              <ul v-else class="ws-mini-tree">
                <li
                  v-for="row in cardTreeRows(ws.id)"
                  :key="row.key"
                  class="ws-mini-row"
                  :style="{ paddingLeft: (2 + row.depth * 16) + 'px' }"
                >
                  <template v-if="row.type === 'folder'">
                    <input
                      type="checkbox"
                      class="fs-check"
                      :checked="cardFolderChecked(ws.id, row)"
                      :indeterminate.prop="cardFolderIndeterminate(ws.id, row)"
                      @click.stop
                      @change="toggleCardFolderScope(ws.id, row)"
                    />
                    <span class="ws-mini-folder" @click.stop="toggleCardFolder(ws.id, row.path)">
                      <span class="fs-chevron" :class="{ 'fs-chevron--open': (cardExpanded[ws.id] || []).includes(row.path) }">
                        <svg width="9" height="9" viewBox="0 0 16 16" fill="none"><path d="M6 3l5 5-5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                      </span>
                      <span class="ws-mini-name ws-mini-name--folder">{{ row.name }}</span>
                      <span class="ws-mini-count">{{ row.count }}</span>
                    </span>
                  </template>
                  <template v-else>
                    <input
                      type="checkbox"
                      class="fs-check"
                      :checked="cardScopeOf(ws.id).includes(row.doc.id)"
                      @click.stop
                      @change="toggleCardDocScope(ws.id, row.doc.id)"
                    />
                    <span class="ws-mini-name" :title="row.doc.relative_path || row.doc.filename">{{ row.doc.filename }}</span>
                  </template>
                </li>
              </ul>
            </div>

            <span
              class="ws-scope-pill"
              :class="{ 'ws-scope-pill--disabled': ws.status !== 'ready' }"
              :title="ws.status !== 'ready' ? 'Disponible une fois le workspace indexé' : ''"
              @click.stop="ws.status === 'ready' && selectForSearch(ws, cardScopeOf(ws.id))"
            >
              {{ cardScopeOf(ws.id).length
                ? `Rechercher dans ${cardScopeOf(ws.id).length} fichier${cardScopeOf(ws.id).length > 1 ? 's' : ''}`
                : 'Rechercher dans ce workspace' }}
            </span>

            <span class="ws-manage-pill" @click.stop="openWorkspace(ws)">
              Gérer le workspace
            </span>
          </button>
          </div>
        </div>

        <p v-if="errorMessage" class="error-msg">{{ errorMessage }}</p>
      </template>

      <!-- ============================================================ -->
      <!-- ========================= DÉTAIL =========================== -->
      <!-- ============================================================ -->
      <template v-else>
        <header class="detail-header">
          <button class="back-link" @click="closeWorkspace">← Retour</button>

          <div class="detail-title-row">
            <template v-if="renaming">
              <input
                ref="renameInput"
                v-model="renameDraft"
                type="text"
                class="rename-input"
                maxlength="120"
                @keyup.enter="commitRename"
                @keyup.esc="cancelRename"
                @blur="commitRename"
              />
              <button class="btn-icon btn-icon--save" @click.stop="commitRename" title="Enregistrer" :disabled="renamingBusy">✓</button>
              <button class="btn-icon" @click.stop="cancelRename" title="Annuler">×</button>
            </template>
            <template v-else>
              <h2 class="detail-title">{{ selectedWorkspace ? selectedWorkspace.name : '' }}</h2>
              <button class="btn-icon btn-icon--ghost" @click="startRename" title="Renommer">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M11 2l3 3-9 9H2v-3l9-9z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>
              </button>
            </template>
          </div>

          <p v-if="renameError" class="rename-error">{{ renameError }}</p>
        </header>

        <!-- Status banners -->
        <div v-if="isProcessing" class="banner banner-info">
          <div class="spinner-mini"></div>
          <span v-if="selectedWorkspace.reorg_source_id">{{ selectedWorkspace.status === 'queued' ? 'Réorganisation en file d\'attente…' : 'Réorganisation en cours… (cela peut prendre plusieurs minutes)' }}</span>
          <span v-else>{{ selectedWorkspace.status === 'queued' ? 'En file d\'attente…' : 'Indexation en cours… (cela peut prendre plusieurs minutes)' }}</span>
        </div>

        <div v-if="selectedWorkspace && selectedWorkspace.status === 'failed'" class="banner banner-error">
          <strong>La dernière indexation a échoué.</strong>
          <pre v-if="selectedWorkspace.ingest_error" class="banner-error-detail">{{ selectedWorkspace.ingest_error }}</pre>
          <button class="btn-secondary" @click="triggerIngest" :disabled="reindexingBusy">
            {{ reindexingBusy ? '…' : 'Relancer l\'indexation' }}
          </button>
        </div>

        <!-- Actions -->
        <div class="detail-actions" v-if="selectedWorkspace">
          <button
            class="action-card"
            :class="{ 'action-card--open': showAddZone }"
            :disabled="isProcessing"
            @click="showAddZone = !showAddZone"
          >
            <span class="action-card-icon">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8h10" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
            </span>
            <span>Ajouter des documents</span>
          </button>
          <button
            class="action-card"
            :disabled="isProcessing || selectedWorkspace.status !== 'ready' || docs.length === 0"
            title="Un agent renomme et range les documents dans une copie du workspace"
            @click="reorgTarget = selectedWorkspace"
          >
            <span class="action-card-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path d="M9.5 2.672a.5.5 0 1 0 1 0V.843a.5.5 0 0 0-1 0v1.829Zm4.5.035A.5.5 0 0 0 13.293 2L12 3.293a.5.5 0 1 0 .707.707L14 2.707ZM7.293 4A.5.5 0 1 0 8 3.293L6.707 2A.5.5 0 0 0 6 2.707L7.293 4Zm-.621 2.5a.5.5 0 1 0 0-1H4.843a.5.5 0 1 0 0 1h1.829Zm8.485 0a.5.5 0 1 0 0-1h-1.829a.5.5 0 0 0 0 1h1.829ZM13.293 10A.5.5 0 1 0 14 9.293L12.707 8a.5.5 0 1 0-.707.707L13.293 10ZM9.5 11.157a.5.5 0 0 0 1 0V9.328a.5.5 0 0 0-1 0v1.829Zm1.854-5.097a.5.5 0 0 0 0-.706l-.708-.708a.5.5 0 0 0-.707 0L8.646 5.94a.5.5 0 0 0 0 .707l.708.708a.5.5 0 0 0 .707 0l1.293-1.293Zm-3 3a.5.5 0 0 0 0-.706l-.708-.708a.5.5 0 0 0-.707 0L.646 13.94a.5.5 0 0 0 0 .707l.708.708a.5.5 0 0 0 .707 0L8.354 9.06Z"/>
              </svg>
            </span>
            <span>Réorganiser avec l'IA</span>
          </button>
          <button
            class="action-card"
            :disabled="isProcessing || selectedWorkspace.status !== 'ready'"
            title="Télécharger l'arborescence du workspace en zip"
            @click="downloadArchiveFor(selectedWorkspace)"
          >
            <span class="action-card-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                <path d="M8 2v8M4.5 6.5L8 10l3.5-3.5M2.5 13h11" />
              </svg>
            </span>
            <span>Télécharger</span>
          </button>
          <button
            v-if="confirmingDeleteWs !== selectedId"
            class="action-card action-card--danger"
            @click="confirmingDeleteWs = selectedId"
          >
            <span class="action-card-icon">
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none"><path d="M3 4h10M5 4V2h6v2M5 4v10h6V4M7 7v5M9 7v5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </span>
            <span>Supprimer</span>
          </button>
          <div v-else class="action-card action-card--confirm">
            <span>Supprimer définitivement&nbsp;?</span>
            <button class="btn-danger" @click="doDeleteSelectedWorkspace" :disabled="deletingId === selectedId">
              {{ deletingId === selectedId ? '…' : 'Oui, supprimer' }}
            </button>
            <button class="btn-ghost-sm" @click="confirmingDeleteWs = null">Annuler</button>
          </div>
        </div>

        <!-- Add zone -->
        <section class="section" v-if="showAddZone">
          <div
            class="add-zone"
            :class="{ active: dragging, disabled: isProcessing }"
            @dragover.prevent="!isProcessing && (dragging = true)"
            @dragleave.prevent="dragging = false"
            @drop.prevent="onDrop"
          >
            <p v-if="!isProcessing">
              <svg width="18" height="18" viewBox="0 0 16 16" fill="none" style="vertical-align: middle;"><path d="M8 1v10M3 6l5-5 5 5M2 15h12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
              Glisse des fichiers ou des dossiers ici, ou choisis
              <button type="button" class="link-btn" @click="$refs.fileInput.click()">des fichiers</button>
              /
              <button type="button" class="link-btn" @click="$refs.folderInput.click()">un dossier</button>
              <span class="add-zone-hint"> · PDF, DOCX, images ({{ maxFileLabel }} max par fichier)</span>
            </p>
            <p v-else class="add-zone-locked">
              ⏳ Indexation en cours, l'ajout est désactivé.
            </p>
            <input
              ref="fileInput"
              type="file"
              accept=".pdf,.docx,.png,.jpg,.jpeg,.webp,.heic,.heif,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg,image/webp,image/heic,image/heif"
              multiple
              style="display: none;"
              @change="onFilePick"
            />
            <input
              ref="folderInput"
              type="file"
              webkitdirectory
              directory
              multiple
              style="display: none;"
              @change="onFolderPick"
            />
          </div>

          <label class="notify-option" v-if="!isProcessing">
            <input type="checkbox" v-model="notifyOnReady" />
            <span>Me notifier par email à la fin de l'indexation</span>
          </label>

          <ul v-if="uploadQueue.length" class="upload-queue">
            <li v-for="(f, i) in uploadQueue" :key="i" :class="['queue-row', 'queue-' + f.status]">
              <div class="queue-main">
                <span class="queue-name" :title="f.relativePath || f.file.name">
                  <span v-if="queueDir(f)" class="doc-path">{{ queueDir(f) }}/</span>{{ f.file.name }}
                </span>
                <span class="queue-size">{{ formatSize(f.file.size) }}</span>
                <span class="queue-icon">
                  <span v-if="f.status === 'pending'">⏳</span>
                  <span v-else-if="f.status === 'uploading'">⬆️</span>
                  <span v-else-if="f.status === 'done'">✅</span>
                  <span v-else-if="f.status === 'error'">❌</span>
                </span>
              </div>
              <div v-if="f.status === 'error' && f.error" class="queue-error-msg">{{ f.error }}</div>
            </li>
          </ul>
        </section>

        <!-- Documents — arborescence -->
        <section class="section">
          <h3 class="section-title">
            Documents
            <span class="docs-count">({{ docs.length }})</span>
          </h3>

          <div v-if="docsLoading" class="state-msg">Chargement…</div>
          <div v-else-if="docs.length === 0" class="state-msg state-msg--small">Aucun document dans ce workspace.</div>

          <ul v-else class="fs-tree">
            <li
              v-for="row in docTreeRows"
              :key="row.key"
              class="fs-row"
              :class="{ 'fs-row--folder': row.type === 'folder' }"
              :style="{ paddingLeft: (10 + row.depth * 22) + 'px' }"
            >
              <template v-if="row.type === 'folder'">
                <input
                  type="checkbox"
                  class="fs-check"
                  :checked="folderChecked(row)"
                  :indeterminate.prop="folderIndeterminate(row)"
                  title="Inclure ce dossier dans le périmètre de recherche"
                  @click.stop
                  @change="toggleFolderScope(row)"
                />
                <button class="fs-folder-btn" @click="toggleFolder(row.path)">
                  <span class="fs-chevron" :class="{ 'fs-chevron--open': expandedFolders.includes(row.path) }">
                    <svg width="10" height="10" viewBox="0 0 16 16" fill="none"><path d="M6 3l5 5-5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  </span>
                  <span class="fs-name fs-name--folder">{{ row.name }}</span>
                  <span class="fs-count">{{ row.count }} fichier{{ row.count > 1 ? 's' : '' }}</span>
                </button>
              </template>
              <template v-else>
                <input
                  type="checkbox"
                  class="fs-check"
                  :checked="scopeSelection.includes(row.doc.id)"
                  title="Inclure ce fichier dans le périmètre de recherche"
                  @click.stop
                  @change="toggleDocScope(row.doc.id)"
                />
                <span class="fs-file-main" :title="row.doc.relative_path || row.doc.filename">
                  <span class="fs-name">{{ row.doc.filename }}</span>
                </span>
                <span class="fs-meta">{{ row.doc.size_bytes ? formatSize(row.doc.size_bytes) : '—' }}</span>
                <span class="fs-actions">
                  <button
                    v-if="row.doc.scaleway_key && isPdf(row.doc.filename)"
                    class="btn-icon btn-icon--ghost"
                    title="Prévisualiser"
                    @click="previewDoc(row.doc)"
                  >
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z" stroke="currentColor" stroke-width="1.3"/><circle cx="8" cy="8" r="2" stroke="currentColor" stroke-width="1.3"/></svg>
                  </button>
                  <template v-if="confirmingDocId === row.doc.id">
                    <button class="btn-danger-sm" @click="deleteDoc(row.doc)" :disabled="deletingDocId === row.doc.id">
                      {{ deletingDocId === row.doc.id ? '…' : 'Confirmer' }}
                    </button>
                    <button class="btn-ghost-sm" :disabled="deletingDocId === row.doc.id" @click="confirmingDocId = null">×</button>
                  </template>
                  <button
                    v-else
                    class="btn-icon btn-icon--ghost btn-icon--danger"
                    :disabled="isProcessing"
                    :title="isProcessing ? 'Indexation en cours' : 'Supprimer'"
                    @click="confirmingDocId = row.doc.id"
                  >
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M3 4h10M5 4V2h6v2M5 4v10h6V4M7 7v5M9 7v5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  </button>
                </span>
              </template>
            </li>
          </ul>
        </section>

        <p v-if="detailError" class="error-msg">{{ detailError }}</p>

        <!-- Barre de recherche sticky — le CTA vit à côté des checkboxes -->
        <div v-if="selectedWorkspace && docs.length > 0" class="scope-bar">
          <span class="scope-bar-info">
            <template v-if="scopeSelection.length">
              <strong>{{ scopeSelection.length }} fichier{{ scopeSelection.length > 1 ? 's' : '' }} coché{{ scopeSelection.length > 1 ? 's' : '' }}</strong>
              <button type="button" class="link-btn scope-clear" @click="scopeSelection = []">tout décocher</button>
            </template>
            <template v-else>
              Coche des dossiers ou des fichiers pour restreindre la recherche
            </template>
          </span>
          <button
            class="scope-search-btn"
            :disabled="isProcessing || selectedWorkspace.status !== 'ready'"
            :title="selectedWorkspace.status !== 'ready' ? 'Disponible une fois le workspace indexé' : ''"
            @click="selectForSearch(selectedWorkspace, scopeSelection)"
          >
            {{ scopeSelection.length
              ? `Rechercher dans ${scopeSelection.length > 1 ? 'ces ' + scopeSelection.length + ' fichiers' : 'ce fichier'}`
              : 'Rechercher dans ce workspace' }}
          </button>
        </div>
      </template>
    </div>

    <!-- Preview PDF d'un doc — réutilise le composant existant -->
    <CitationPreviewModal
      v-if="previewCitation"
      :citation="previewCitation"
      @close="previewCitation = null"
    />

    <!-- Réorganisation IA — même modal que depuis le dropdown du dashboard -->
    <WorkspaceReorganizeModal
      :workspace="reorgTarget"
      @close="reorgTarget = null"
      @created="onReorgCreated"
    />
  </div>
</template>

<script>
import axios from 'axios';
import { supabase } from '@/supabase/supabaseClient';
import { getSupabaseToken } from '@/services/api';
import CitationPreviewModal from './CitationPreviewModal.vue';
import WorkspaceReorganizeModal from './WorkspaceReorganizeModal.vue';
import {
  MAX_UPLOAD_FILE_LABEL,
  MAX_DOCS_PER_WORKSPACE,
  isTooLarge,
  tooLargeMessage,
  uploadErrorMessage,
} from '@/utils/workspaceUpload';

const API_URL = process.env.VUE_APP_API_URL || 'https://api.query-juriste.com';
const ALLOWED_EXT = ['.pdf', '.docx', '.png', '.jpg', '.jpeg', '.webp', '.heic', '.heif'];

export default {
  name: 'WorkspaceManageModal',
  components: { CitationPreviewModal, WorkspaceReorganizeModal },
  props: {
    // Ouvre directement la vue détail de ce workspace (depuis le dropdown du dashboard).
    initialWorkspaceId: { type: String, default: null },
  },
  data() {
    return {
      // Liste
      items: [],
      loading: true,
      errorMessage: '',
      searchQuery: '',
      // Mini-arbres des cards : docs chargés à la volée + état par workspace
      cardTrees: {},
      cardTreeOpen: [],
      cardTreeLoading: [],
      cardExpanded: {},
      cardScope: {},

      // Détail
      selectedId: null,
      selectedWorkspace: null,
      docs: [],
      docsLoading: false,
      detailError: '',
      pollingInterval: null,
      expandedFolders: [],
      // Périmètre de recherche : doc_ids cochés dans l'arbre (vide = tout)
      scopeSelection: [],

      // Suppression workspace (depuis détail)
      confirmingDeleteWs: null,
      deletingId: null,

      // Suppression doc individuel
      confirmingDocId: null,
      deletingDocId: null,

      // Rename
      renaming: false,
      renameDraft: '',
      renameError: '',
      renamingBusy: false,

      // Ré-indexation
      reindexingBusy: false,

      // Upload
      showAddZone: false,
      dragging: false,
      uploadQueue: [],
      uploading: false,
      notifyOnReady: true,

      // Réorganisation IA (modal partagé avec le dropdown du dashboard)
      reorgTarget: null,

      // Preview
      previewCitation: null,
    };
  },
  computed: {
    maxFileLabel() {
      return MAX_UPLOAD_FILE_LABEL;
    },
    isProcessing() {
      return !!this.selectedWorkspace && ['queued', 'ingesting'].includes(this.selectedWorkspace.status);
    },
    filteredItems() {
      let arr = [...this.items];
      const q = this.searchQuery.trim().toLowerCase();
      if (q) {
        arr = arr.filter((w) => (w.name || '').toLowerCase().includes(q));
      }
      arr.sort((a, b) => new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0));
      return arr;
    },
    // Arborescence des documents (vue détail), aplatie en lignes.
    docTreeRows() {
      return this.buildTreeRows(this.docs, this.expandedFolders);
    },
  },
  async mounted() {
    window.addEventListener('keydown', this.onKeyDown);
    await this.fetchList();
    if (this.initialWorkspaceId) {
      const ws = this.items.find((w) => w.id === this.initialWorkspaceId);
      if (ws) await this.openWorkspace(ws);
    }
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onKeyDown);
    this.stopPolling();
  },
  methods: {
    onKeyDown(e) {
      if (e.key !== 'Escape') return;
      if (this.previewCitation) return; // Le preview gère son propre escape
      if (this.renaming) { this.cancelRename(); return; }
      if (this.confirmingDocId) { this.confirmingDocId = null; return; }
      if (this.confirmingDeleteWs) { this.confirmingDeleteWs = null; return; }
      this.closeModal();
    },
    closeModal() {
      this.stopPolling();
      this.$emit('close');
    },

    // ─────────── Liste ───────────
    async fetchList() {
      this.loading = true;
      this.errorMessage = '';
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('not authenticated');
        const { data, error } = await supabase
          .from('workspaces')
          .select('id, name, status, pdf_count, total_pages, ingest_error, created_at, updated_at, reorg_source_id')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });
        if (error) throw error;
        this.items = data || [];
      } catch (e) {
        console.error('workspace list fetch failed', e);
        this.errorMessage = 'Impossible de charger la liste des workspaces.';
      } finally {
        this.loading = false;
      }
    },

    // ─────────── Actions des cards ───────────
    async downloadArchiveFor(ws) {
      try {
        const token = await getSupabaseToken();
        const resp = await axios.post(
          `${API_URL}/copilot/api/v1/workspace/${ws.id}/archive-url`,
          {},
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (resp.data && resp.data.url) window.open(resp.data.url, '_blank');
      } catch (e) {
        console.error('archive download failed', e);
        if (this.$toast) this.$toast.error("L'archive n'est pas (encore) disponible pour ce workspace.");
      }
    },

    async deleteWorkspaceFromList(ws) {
      if (!window.confirm(`Supprimer « ${ws.name || 'ce workspace'} » ? Cette action est irréversible.`)) return;
      try {
        const token = await getSupabaseToken();
        await axios.delete(`${API_URL}/copilot/api/v1/workspace/${ws.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        this.items = this.items.filter((w) => w.id !== ws.id);
        this.$emit('workspace-deleted', { id: ws.id });
        if (this.$toast) this.$toast.success('Workspace supprimé.');
      } catch (e) {
        console.error('workspace delete failed', e);
        if (this.$toast) this.$toast.error('La suppression a échoué.');
      }
    },

    // ─────────── Détail ───────────
    async openWorkspace(ws) {
      this.selectedId = ws.id;
      this.selectedWorkspace = ws;
      this.confirmingDocId = null;
      this.deletingDocId = null;
      this.uploadQueue = [];
      this.detailError = '';
      this.showAddZone = false;
      this.expandedFolders = [];
      this.scopeSelection = [];
      this.confirmingDeleteWs = null;
      await Promise.all([this.fetchSelectedWorkspace(), this.fetchDocs()]);
      if (this.isProcessing) this.startPolling();
    },

    closeWorkspace() {
      this.stopPolling();
      this.selectedId = null;
      this.selectedWorkspace = null;
      this.docs = [];
      this.uploadQueue = [];
      this.detailError = '';
      this.renaming = false;
      this.renameError = '';
      this.showAddZone = false;
      this.expandedFolders = [];
      this.scopeSelection = [];
      this.fetchList();
    },

    selectForSearch(ws, scope = null) {
      // Le dashboard sélectionne le workspace pour la recherche et ferme le
      // modal. `scope` = doc_ids cochés (mini-arbre d'une card ou arbre de la
      // vue détail) ; null/vide = tout le workspace.
      this.$emit('workspace-selected', {
        ...ws,
        scope: scope && scope.length ? [...scope] : null,
      });
    },

    // ─────────── Constructeur d'arbre partagé (détail + mini-arbres cards) ──
    buildTreeRows(docs, expandedPaths) {
      const rootFolders = new Map();
      const rootFiles = [];
      for (const d of docs) {
        const rel = (d.relative_path || '').replace(/^\/+|\/+$/g, '');
        const parts = rel ? rel.split('/') : [];
        const folderParts = parts.length > 1 ? parts.slice(0, -1) : [];
        if (!folderParts.length) {
          rootFiles.push(d);
          continue;
        }
        let map = rootFolders;
        let node = null;
        let path = '';
        for (const p of folderParts) {
          path = path ? `${path}/${p}` : p;
          if (!map.has(p)) map.set(p, { name: p, path, folders: new Map(), files: [] });
          node = map.get(p);
          map = node.folders;
        }
        node.files.push(d);
      }

      const countFiles = (n) => n.files.length + [...n.folders.values()].reduce((acc, c) => acc + countFiles(c), 0);
      const byName = (a, b) => a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' });
      const byFile = (a, b) => (a.filename || '').localeCompare(b.filename || '', 'fr', { sensitivity: 'base' });

      const collectIds = (n) => [
        ...n.files.map((d) => d.id),
        ...[...n.folders.values()].flatMap(collectIds),
      ];

      const rows = [];
      const walk = (folders, files, depth) => {
        for (const f of [...folders.values()].sort(byName)) {
          rows.push({
            type: 'folder',
            key: `f:${f.path}`,
            name: f.name,
            path: f.path,
            depth,
            count: countFiles(f),
            fileIds: collectIds(f),
          });
          if (expandedPaths.includes(f.path)) walk(f.folders, f.files, depth + 1);
        }
        for (const d of [...files].sort(byFile)) {
          rows.push({ type: 'file', key: d.id, doc: d, depth });
        }
      };
      walk(rootFolders, rootFiles, 0);
      return rows;
    },

    // ─────────── Mini-arbre des cards ───────────
    async toggleCardTree(ws) {
      if (this.cardTreeOpen.includes(ws.id)) {
        this.cardTreeOpen = this.cardTreeOpen.filter((id) => id !== ws.id);
        return;
      }
      this.cardTreeOpen = [...this.cardTreeOpen, ws.id];
      if (!this.cardTrees[ws.id] && !this.cardTreeLoading.includes(ws.id)) {
        this.cardTreeLoading = [...this.cardTreeLoading, ws.id];
        try {
          const { data, error } = await supabase
            .from('workspace_documents')
            .select('id, filename, relative_path')
            .eq('workspace_id', ws.id)
            .order('created_at', { ascending: true });
          if (error) throw error;
          this.$set(this.cardTrees, ws.id, data || []);
        } catch (e) {
          console.error('card tree fetch failed', e);
          if (this.$toast) this.$toast.error('Impossible de charger les fichiers.');
          this.cardTreeOpen = this.cardTreeOpen.filter((id) => id !== ws.id);
        } finally {
          this.cardTreeLoading = this.cardTreeLoading.filter((id) => id !== ws.id);
        }
      }
    },
    cardTreeRows(wsId) {
      return this.buildTreeRows(this.cardTrees[wsId] || [], this.cardExpanded[wsId] || []);
    },
    toggleCardFolder(wsId, path) {
      const cur = this.cardExpanded[wsId] || [];
      this.$set(
        this.cardExpanded,
        wsId,
        cur.includes(path) ? cur.filter((p) => p !== path) : [...cur, path],
      );
    },
    cardScopeOf(wsId) {
      return this.cardScope[wsId] || [];
    },
    toggleCardDocScope(wsId, docId) {
      const cur = this.cardScopeOf(wsId);
      this.$set(
        this.cardScope,
        wsId,
        cur.includes(docId) ? cur.filter((id) => id !== docId) : [...cur, docId],
      );
    },
    cardFolderChecked(wsId, row) {
      const scope = this.cardScopeOf(wsId);
      return row.fileIds.length > 0 && row.fileIds.every((id) => scope.includes(id));
    },
    cardFolderIndeterminate(wsId, row) {
      const scope = this.cardScopeOf(wsId);
      const n = row.fileIds.filter((id) => scope.includes(id)).length;
      return n > 0 && n < row.fileIds.length;
    },
    toggleCardFolderScope(wsId, row) {
      const cur = this.cardScopeOf(wsId);
      this.$set(
        this.cardScope,
        wsId,
        this.cardFolderChecked(wsId, row)
          ? cur.filter((id) => !row.fileIds.includes(id))
          : [...new Set([...cur, ...row.fileIds])],
      );
    },
    searchFromCard(ws) {
      // Un workspace pas prêt ne peut pas être interrogé : le clic ouvre le
      // détail (suivi de progression) au lieu de lancer une recherche.
      if (ws.status !== 'ready') {
        this.openWorkspace(ws);
        return;
      }
      this.selectForSearch(ws, this.cardScopeOf(ws.id));
    },

    // ─────────── Périmètre de recherche (arbre) ───────────
    toggleDocScope(docId) {
      if (this.scopeSelection.includes(docId)) {
        this.scopeSelection = this.scopeSelection.filter((id) => id !== docId);
      } else {
        this.scopeSelection = [...this.scopeSelection, docId];
      }
    },
    folderChecked(row) {
      return row.fileIds.length > 0 && row.fileIds.every((id) => this.scopeSelection.includes(id));
    },
    folderIndeterminate(row) {
      const n = row.fileIds.filter((id) => this.scopeSelection.includes(id)).length;
      return n > 0 && n < row.fileIds.length;
    },
    toggleFolderScope(row) {
      const allSelected = this.folderChecked(row);
      if (allSelected) {
        this.scopeSelection = this.scopeSelection.filter((id) => !row.fileIds.includes(id));
      } else {
        this.scopeSelection = [...new Set([...this.scopeSelection, ...row.fileIds])];
      }
    },

    toggleFolder(path) {
      if (this.expandedFolders.includes(path)) {
        this.expandedFolders = this.expandedFolders.filter((p) => p !== path);
      } else {
        this.expandedFolders = [...this.expandedFolders, path];
      }
    },

    onReorgCreated(workspace) {
      this.reorgTarget = null;
      this.$emit('workspace-created', workspace);
      if (this.$toast) this.$toast.success('Réorganisation lancée — un nouveau workspace a été créé.');
      this.openWorkspace({ id: workspace.id, name: workspace.name, status: workspace.status || 'queued' });
    },

    async fetchSelectedWorkspace() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        const { data, error } = await supabase
          .from('workspaces')
          .select('id, name, status, pdf_count, total_pages, ingest_error, created_at, updated_at, reorg_source_id')
          .eq('id', this.selectedId)
          .eq('user_id', user.id)
          .single();
        if (error) throw error;
        this.selectedWorkspace = data;
      } catch (e) {
        console.error('selected workspace fetch failed', e);
        this.detailError = 'Impossible de charger le workspace.';
      }
    },

    async fetchDocs() {
      this.docsLoading = true;
      try {
        const { data, error } = await supabase
          .from('workspace_documents')
          .select('id, filename, size_bytes, status, created_at, scaleway_key, enabled, relative_path')
          .eq('workspace_id', this.selectedId)
          .order('created_at', { ascending: true });
        if (error) throw error;
        this.docs = data || [];
      } catch (e) {
        console.error('docs fetch failed', e);
        this.detailError = 'Impossible de charger les documents.';
      } finally {
        this.docsLoading = false;
      }
    },

    startPolling() {
      this.stopPolling();
      this.pollingInterval = setInterval(async () => {
        await this.fetchSelectedWorkspace();
        if (!this.isProcessing) {
          this.stopPolling();
          await this.fetchDocs();
        }
      }, 5000);
    },

    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },

    // ─────────── Rename ───────────
    startRename() {
      if (!this.selectedWorkspace) return;
      this.renameDraft = this.selectedWorkspace.name || '';
      this.renameError = '';
      this.renaming = true;
      this.$nextTick(() => {
        if (this.$refs.renameInput) {
          this.$refs.renameInput.focus();
          this.$refs.renameInput.select();
        }
      });
    },
    cancelRename() {
      this.renaming = false;
      this.renameDraft = '';
      this.renameError = '';
    },
    async commitRename() {
      if (!this.renaming) return;
      const name = (this.renameDraft || '').trim();
      if (!name) {
        this.renameError = 'Le nom ne peut pas être vide.';
        return;
      }
      if (name === (this.selectedWorkspace && this.selectedWorkspace.name)) {
        this.cancelRename();
        return;
      }
      if (name.length > 120) {
        this.renameError = 'Maximum 120 caractères.';
        return;
      }
      this.renamingBusy = true;
      this.renameError = '';
      try {
        const token = await getSupabaseToken();
        await axios.patch(
          `${API_URL}/copilot/api/v1/workspace/${this.selectedId}`,
          { name },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (this.selectedWorkspace) this.selectedWorkspace.name = name;
        const idx = this.items.findIndex((w) => w.id === this.selectedId);
        if (idx >= 0) this.items[idx].name = name;
        this.renaming = false;
        this.renameDraft = '';
        if (this.$toast) this.$toast.success('Workspace renommé.');
      } catch (e) {
        console.error('rename failed', e);
        this.renameError = (e.response && e.response.data && e.response.data.error) || 'Renommage échoué.';
      } finally {
        this.renamingBusy = false;
      }
    },

    // ─────────── Preview PDF ───────────
    previewDoc(d) {
      if (!d.scaleway_key) return;
      this.previewCitation = {
        nodeId: d.id,
        workspaceId: this.selectedId,
        title: d.filename,
        source_file: d.filename,
        scaleway_key: d.scaleway_key,
        page_start: null,
        page_end: null,
      };
    },

    // ─────────── Suppression workspace entier ───────────
    async doDeleteSelectedWorkspace() {
      if (!this.selectedId) return;
      this.deletingId = this.selectedId;
      try {
        const token = await getSupabaseToken();
        await axios.delete(`${API_URL}/copilot/api/v1/workspace/${this.selectedId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        this.items = this.items.filter((w) => w.id !== this.selectedId);
        this.$emit('workspace-deleted', { id: this.selectedId });
        if (this.$toast) this.$toast.success('Workspace supprimé.');
        this.closeWorkspace();
      } catch (e) {
        console.error('workspace delete failed', e);
        this.detailError = 'La suppression a échoué.';
      } finally {
        this.deletingId = null;
        this.confirmingDeleteWs = null;
      }
    },

    // ─────────── Suppression doc individuel ───────────
    async deleteDoc(d) {
      if (this.isProcessing) {
        this.detailError = 'Indexation en cours, suppression impossible.';
        return;
      }
      this.deletingDocId = d.id;
      this.detailError = '';
      const wasReady = this.selectedWorkspace && this.selectedWorkspace.status === 'ready';
      try {
        const token = await getSupabaseToken();
        await axios.delete(
          `${API_URL}/copilot/api/v1/workspace/${this.selectedId}/document/${d.id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        this.confirmingDocId = null;
        await this.fetchDocs();
        await this.fetchSelectedWorkspace();
        if (wasReady && this.docs.length > 0) {
          await this.triggerIngest();
        }
      } catch (e) {
        const status = e.response && e.response.status;
        if (status === 409) {
          this.detailError = 'Indexation en cours, réessaye dans quelques minutes.';
        } else {
          console.error('deleteDoc failed', e);
          this.detailError = 'Suppression échouée.';
        }
      } finally {
        this.deletingDocId = null;
      }
    },

    // ─────────── Upload ───────────
    onFilePick(e) {
      const files = Array.from(e.target.files || []).map((file) => ({ file, relativePath: file.name }));
      this.addToQueue(files);
      e.target.value = '';
      this.uploadAll();
    },

    onFolderPick(e) {
      const files = Array.from(e.target.files || []).map((file) => ({
        file,
        relativePath: file.webkitRelativePath || file.name,
      }));
      this.addToQueue(files);
      e.target.value = '';
      this.uploadAll();
    },

    async onDrop(e) {
      this.dragging = false;
      if (this.isProcessing) return;
      // Dossiers droppés : parcours récursif via webkitGetAsEntry (même
      // mécanique que le modal de création) pour capturer les chemins relatifs.
      const items = e.dataTransfer.items;
      if (items && items.length && items[0].webkitGetAsEntry) {
        const entries = [];
        for (let i = 0; i < items.length; i += 1) {
          const entry = items[i].webkitGetAsEntry && items[i].webkitGetAsEntry();
          if (entry) entries.push(entry);
        }
        const collected = [];
        await Promise.all(entries.map((entry) => this.walkEntry(entry, '', collected)));
        this.addToQueue(collected);
      } else {
        const files = Array.from(e.dataTransfer.files || []).map((file) => ({ file, relativePath: file.name }));
        this.addToQueue(files);
      }
      this.uploadAll();
    },

    walkEntry(entry, prefix, out) {
      return new Promise((resolve) => {
        if (entry.isFile) {
          entry.file((file) => {
            out.push({ file, relativePath: prefix ? `${prefix}/${file.name}` : file.name });
            resolve();
          }, () => resolve());
        } else if (entry.isDirectory) {
          const reader = entry.createReader();
          const collected = [];
          const readBatch = () => {
            reader.readEntries((batch) => {
              if (!batch.length) {
                Promise.all(collected.map((ee) => this.walkEntry(ee, prefix ? `${prefix}/${entry.name}` : entry.name, out))).then(resolve);
                return;
              }
              collected.push(...batch);
              readBatch();
            }, () => resolve());
          };
          readBatch();
        } else {
          resolve();
        }
      });
    },

    addToQueue(entries) {
      const extOk = entries.filter((e) => {
        const n = (e.file.name || '').toLowerCase();
        return ALLOWED_EXT.some((ext) => n.endsWith(ext));
      });
      const skippedExt = entries.length - extOk.length;
      if (skippedExt > 0 && this.$toast) this.$toast.info(`${skippedExt} fichier(s) ignoré(s) (format non supporté)`);

      // Les fichiers trop gros restent visibles dans la file, en erreur, avec
      // la raison écrite en clair (sinon l'utilisateur ne comprend pas).
      const tooLarge = extOk.filter((e) => isTooLarge(e.file));
      const sizeOk = extOk.filter((e) => !isTooLarge(e.file));
      if (tooLarge.length > 0 && this.$toast) {
        this.$toast.error(`${tooLarge.length} fichier(s) trop volumineux : ${MAX_UPLOAD_FILE_LABEL} maximum par fichier.`);
      }

      const remaining = Math.max(0, MAX_DOCS_PER_WORKSPACE - this.docs.length - this.uploadQueue.filter((q) => q.status !== 'error').length);
      const accepted = sizeOk.slice(0, remaining);
      const overflow = sizeOk.length - accepted.length;
      if (overflow > 0 && this.$toast) this.$toast.error(`${overflow} fichier(s) ignoré(s) : limite de ${MAX_DOCS_PER_WORKSPACE} fichiers par workspace`);

      this.uploadQueue.push(...tooLarge.map(({ file, relativePath }) => ({
        file,
        relativePath: relativePath || file.name,
        status: 'error',
        error: tooLargeMessage(file),
      })));
      this.uploadQueue.push(...accepted.map(({ file, relativePath }) => ({
        file,
        relativePath: relativePath || file.name,
        status: 'pending',
        error: null,
      })));
    },

    queueDir(f) {
      const rel = f.relativePath || '';
      const idx = rel.lastIndexOf('/');
      return idx > 0 ? rel.slice(0, idx) : '';
    },

    async uploadAll() {
      if (this.uploading) return;
      if (this.isProcessing) {
        if (this.$toast) this.$toast.warning('Indexation en cours, attends quelques minutes avant d\'ajouter.');
        return;
      }
      this.uploading = true;
      // Figé au départ : si l'utilisateur revient à la liste (selectedId remis
      // à null) ou ouvre un autre workspace pendant l'envoi, les fichiers
      // restants partent quand même vers le bon workspace (et plus jamais
      // vers /workspace/null/upload).
      const wsId = this.selectedId;
      const queue = this.uploadQueue;
      const token = await getSupabaseToken();
      let uploadedCount = 0;
      let failedCount = 0;
      let aborted409 = false;
      for (const entry of queue) {
        if (entry.status !== 'pending') continue;
        entry.status = 'uploading';
        let resp = null;
        try {
          const form = new FormData();
          form.append('file', entry.file);
          if (entry.relativePath && entry.relativePath !== entry.file.name) {
            form.append('relative_path', entry.relativePath);
          }
          resp = await fetch(`${API_URL}/copilot/api/v1/workspace/${wsId}/upload`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: form,
          });
          if (resp.ok) {
            entry.status = 'done';
            uploadedCount += 1;
            continue;
          }
          entry.status = 'error';
          entry.error = await uploadErrorMessage({ resp, file: entry.file });
        } catch (e) {
          entry.status = 'error';
          entry.error = await uploadErrorMessage({ resp, error: e, file: entry.file });
        }
        failedCount += 1;
        console.error('upload failed', entry.file.name, resp && resp.status, entry.error);
        if (resp && resp.status === 409) {
          // Indexation en cours ou workspace plein : les suivants échoueraient aussi.
          aborted409 = true;
          break;
        }
      }
      if (uploadedCount > 0) {
        // Ajout à un workspace déjà indexé → indexation incrémentale : seuls
        // les nouveaux fichiers sont traités puis greffés dans l'arbre
        // existant (guides + résumé global régénérés côté job).
        await this.triggerIngest(true, wsId);
      }
      this.uploading = false;
      if (aborted409 && this.$toast) {
        this.$toast.warning('Envoi interrompu : voir le détail sous les fichiers.');
      } else if (failedCount > 0 && this.$toast) {
        this.$toast.error(`${failedCount} fichier(s) n'ont pas pu être envoyés : voir le détail sous chaque fichier.`);
      }
      if (uploadedCount > 0 && this.$toast) {
        this.$toast.success(`${uploadedCount} fichier(s) ajouté(s) : indexation lancée`);
      }
      if (this.selectedId === wsId) await this.fetchDocs();
    },

    async triggerIngest(incremental, workspaceId) {
      // Garde : les boutons « Ré-indexer / Relancer » appellent triggerIngest
      // directement depuis @click, l'argument est alors un MouseEvent → full.
      const inc = incremental === true;
      const wsId = typeof workspaceId === 'string' ? workspaceId : this.selectedId;
      if (!wsId) return;
      this.reindexingBusy = true;
      try {
        const token = await getSupabaseToken();
        const resp = await fetch(`${API_URL}/copilot/api/v1/workspace/${wsId}/ingest`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ notify: this.notifyOnReady, incremental: inc }),
        });
        if (!resp.ok && resp.status !== 409) {
          console.error('ingest trigger failed', resp.status, await resp.text());
        }
        if (this.selectedId === wsId) {
          await this.fetchSelectedWorkspace();
          if (this.isProcessing) this.startPolling();
        }
      } catch (e) {
        console.error('ingest trigger failed', e);
      } finally {
        this.reindexingBusy = false;
      }
    },

    // ─────────── Helpers ───────────
    formatDate(iso) {
      if (!iso) return '';
      try {
        return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
      } catch (e) {
        return '';
      }
    },
    formatSize(b) {
      if (!b) return '0 B';
      if (b < 1024) return b + ' B';
      if (b < 1024 * 1024) return (b / 1024).toFixed(1) + ' KB';
      if (b < 1024 * 1024 * 1024) return (b / 1024 / 1024).toFixed(1) + ' MB';
      return (b / 1024 / 1024 / 1024).toFixed(2) + ' GB';
    },
    isPdf(filename) {
      return (filename || '').toLowerCase().endsWith('.pdf');
    },
  },
};
</script>

<style scoped>
/* ───────────── Shell ───────────── */
.explorer-overlay {
  position: fixed;
  top: 0; left: 0; width: 100%; height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex; justify-content: center; align-items: center;
  z-index: 1000;
  padding: 24px;
  box-sizing: border-box;
}
.explorer-shell {
  position: relative;
  background: #fff;
  border-radius: 12px;
  width: 100%;
  max-width: 820px;
  height: 100%;
  max-height: 92vh;
  overflow-y: auto;
  padding: 32px 36px;
  box-sizing: border-box;
  box-shadow: 0 20px 60px rgba(0,0,0,0.35);
}
.close-icon {
  position: absolute;
  top: 14px; right: 18px;
  background: transparent; border: none;
  font-size: 1.9rem; line-height: 1; color: #9ca3af;
  cursor: pointer; padding: 2px 8px;
  z-index: 5;
}
.close-icon:hover { color: #374151; }

/* ───────────── Header liste : titre + recherche côte à côte ───────── */
.exp-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 18px;
  margin-bottom: 16px;
  padding-right: 28px; /* laisse la place au bouton fermer */
  flex-wrap: wrap;
}
.exp-title {
  margin: 0;
  font-family: 'Poppins', ui-sans-serif, system-ui, sans-serif;
  font-size: 1.3rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #111827;
}
/* Même recherche que « Rechercher une recherche précédente » du dashboard */
.dossier-search-container {
  position: relative;
  display: flex;
  align-items: center;
}
.search-icon-grey {
  position: absolute;
  left: 12px;
  color: #a8aebf;
  pointer-events: none;
}
.dossier-search-input {
  width: 250px;
  height: 36px;
  border-radius: 18px;
  border: 1px solid #dcdfe6;
  padding-left: 35px;
  padding-right: 15px;
  font-size: 0.85rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.dossier-search-input:focus {
  outline: none;
  border-color: #6c63ff;
  box-shadow: 0 0 0 2px rgba(108, 99, 255, 0.2);
}

/* ───────────── Grille workspaces (même langage que les cards de recherche
   du dashboard : finder-main + finder-card) ───────────── */
.ws-finder {
  background: linear-gradient(to right, #E9EFF4, #E2FAFFCC);
  border-radius: 12px;
  padding: 18px;
  min-height: 220px;
  box-shadow:
    inset 0 1px 3px rgba(0, 0, 0, 0.04),
    0 1px 2px rgba(0, 0, 0, 0.03);
}
.ws-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
  align-content: start;
}
.ws-card {
  background: #fff;
  border: 1px solid #e0e4e8;
  border-radius: 12px;
  padding: 15px;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
  position: relative;
  min-height: 100px;
  display: flex;
  flex-direction: column;
  font: inherit;
}
.ws-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(108, 99, 255, 0.3);
}
.ws-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
}
.ws-card-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.ws-date-chip {
  font-size: 0.7rem;
  color: #6c63ff;
  background: linear-gradient(135deg, rgba(108, 99, 255, 0.08), rgba(108, 99, 255, 0.12));
  padding: 3px 10px;
  border-radius: 12px;
  white-space: nowrap;
  font-weight: 500;
  border: 1px solid rgba(108, 99, 255, 0.15);
}
.ws-mini-spinner {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
  border: 2px solid #6c63ff;
  border-right-color: transparent;
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
}
.ws-fail-dot {
  width: 9px;
  height: 9px;
  flex-shrink: 0;
  border-radius: 50%;
  background: #ff5252;
}
/* Actions inline — même vocabulaire que les lignes du dropdown workspace */
.ws-card-actions {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}
.ws-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  color: #9ca3af;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
}
.ws-action-btn:hover { background: #f5f3ff; color: #6c63ff; }
.ws-action-btn--danger:hover { background: #fef2f2; color: #ff5252; }
.ws-action-btn--disabled { opacity: 0.4; cursor: not-allowed; }
.ws-action-btn--disabled:hover { background: transparent; color: #9ca3af; }
.ws-card-body {
  flex: 1;
  display: flex;
  align-items: flex-start;
  gap: 7px;
  min-width: 0;
}
.ws-tree-chevron {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  margin-top: 2px;
  flex-shrink: 0;
  border-radius: 5px;
  color: #9ca3af;
  transition: transform 0.15s, color 0.12s, background 0.12s;
}
.ws-tree-chevron:hover { background: #f5f3ff; color: #6c63ff; }
.ws-tree-chevron--open { transform: rotate(90deg); color: #6c63ff; }
.ws-card-name {
  font-size: 0.9rem;
  font-weight: 500;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

/* Mini-arbre déplié dans la card */
.ws-card-tree {
  margin-top: 8px;
  padding: 6px 4px;
  background: #fafaff;
  border: 1px solid #eef0f4;
  border-radius: 8px;
  max-height: 170px;
  overflow-y: auto;
  cursor: default;
}
.ws-tree-loading {
  padding: 8px;
  font-size: 0.76rem;
  color: #9ca3af;
}
.ws-mini-tree { list-style: none; margin: 0; padding: 0; }
.ws-mini-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding-top: 3px;
  padding-bottom: 3px;
  font-size: 0.78rem;
  min-width: 0;
}
.ws-mini-folder {
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
  flex: 1;
  cursor: pointer;
}
.ws-mini-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #374151;
}
.ws-mini-name--folder { font-weight: 600; color: #111827; }
.ws-mini-count {
  margin-left: auto;
  padding-right: 6px;
  font-size: 0.68rem;
  color: #9ca3af;
  flex-shrink: 0;
}

/* Recherche — bouton pill violet toujours visible au-dessus de Gérer.
   Libellé : « ce workspace » sans coche, « N fichiers » avec sélection. */
.ws-scope-pill {
  display: block;
  margin-top: 10px;
  padding: 7px 12px;
  text-align: center;
  background: #fff;
  color: #6c63ff;
  border: 1px solid rgba(108, 99, 255, 0.5);
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}
.ws-scope-pill:hover:not(.ws-scope-pill--disabled) {
  background: linear-gradient(135deg, #6c63ff, #5a52d5);
  border-color: transparent;
  color: #fff;
}
.ws-scope-pill--disabled { opacity: 0.45; cursor: not-allowed; }

/* Gérer le workspace — pill neutre en pied de card (le clic card = recherche) */
.ws-manage-pill {
  display: block;
  margin-top: 10px;
  padding: 7px 12px;
  text-align: center;
  background: #fff;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}
.ws-manage-pill:hover {
  border-color: #6c63ff;
  color: #6c63ff;
  background: #f8f7ff;
}

/* ───────────── États ───────────── */
.state-msg {
  padding: 40px 0; text-align: center; color: #6b7280;
}
.state-msg--small { padding: 20px 0; font-size: 0.875rem; }
.empty-state {
  text-align: center; padding: 60px 20px;
}
.empty-state h3 { margin: 0 0 8px; color: #111827; font-size: 1.1rem; }
.empty-state p { margin: 0; color: #6b7280; font-size: 0.9rem; }

/* ───────────── Détail header ───────────── */
.detail-header { margin-bottom: 20px; }
.back-link {
  background: transparent; border: none;
  color: #6c63ff; font-size: 0.85rem;
  cursor: pointer; padding: 0 0 12px 0;
  display: inline-block;
}
.back-link:hover { text-decoration: underline; }
.detail-title-row {
  display: flex; align-items: center; gap: 8px;
}
.detail-title {
  margin: 0;
  font-size: 1.55rem;
  font-weight: 700;
  color: #111827;
  flex: 0 1 auto;
  max-width: 80%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rename-input {
  flex: 1;
  max-width: 480px;
  padding: 6px 10px;
  border: 1px solid #6c63ff;
  border-radius: 6px;
  font-size: 1.2rem;
  font-weight: 600;
  color: #111827;
  outline: none;
}
.rename-error { color: #b91c1c; font-size: 0.82rem; margin: 4px 0 0; }

/* ───────────── Banners ───────────── */
.banner {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 14px; border-radius: 8px;
  font-size: 0.88rem;
  margin-bottom: 14px;
}
.banner-info { background: #f8f7ff; border: 1px solid #ddd9ff; color: #5a52d5; }
.banner-warning {
  background: #fffbeb; border: 1px solid #fde68a; color: #92400e;
  justify-content: space-between;
}
.banner-error { background: #fef2f2; border: 1px solid #fca5a5; color: #991b1b; flex-direction: column; align-items: stretch; gap: 8px; }
.banner-error strong { display: block; }
.banner-error-detail {
  background: #fff; border: 1px solid #fecaca; border-radius: 6px;
  padding: 8px 10px; margin: 4px 0 0;
  font-family: ui-monospace, monospace; font-size: 0.75rem;
  white-space: pre-wrap; max-height: 120px; overflow-y: auto;
  color: #7f1d1d;
}
.spinner-mini {
  width: 14px; height: 14px;
  border: 2px solid currentColor; border-right-color: transparent;
  border-radius: 50%; animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ───────────── Sections ───────────── */
.section { margin-bottom: 26px; }
.section-title {
  font-size: 0.92rem; font-weight: 600;
  color: #374151; margin: 0 0 10px;
  display: flex; align-items: center; gap: 8px;
}
.docs-count { color: #6b7280; font-weight: 400; }

/* Drop zone */
.add-zone {
  border: 2px dashed #d1d5db; border-radius: 8px;
  padding: 18px 16px; text-align: center;
  color: #6b7280; transition: all 0.15s;
  margin-bottom: 8px; font-size: 0.9rem;
}
.add-zone.active { border-color: #6c63ff; background: #f5f3ff; color: #6c63ff; }
.add-zone.disabled { opacity: 0.55; cursor: not-allowed; }
.add-zone p { margin: 0; }
.add-zone-hint { color: #9ca3af; font-size: 0.78rem; }
.add-zone-locked { color: #92400e; }
.link-btn {
  background: transparent; border: none; color: #6c63ff;
  text-decoration: underline; cursor: pointer; padding: 0; font: inherit;
}

.notify-option {
  display: flex; align-items: center; gap: 8px;
  margin: 0 0 14px 2px;
  font-size: 0.82rem; color: #4b5563;
  cursor: pointer; user-select: none;
}
.notify-option input[type="checkbox"] { margin: 0; cursor: pointer; accent-color: #6c63ff; }

.upload-queue {
  list-style: none; padding: 0; margin: 0;
  max-height: 220px; overflow-y: auto;
  border: 1px solid #e0e4e8; border-radius: 6px;
}
.queue-row {
  padding: 6px 10px; font-size: 0.82rem;
  border-bottom: 1px solid #f3f4f6;
}
.queue-main { display: flex; align-items: center; gap: 10px; }
.queue-error-msg { margin-top: 3px; color: #b91c1c; font-size: 0.76rem; line-height: 1.35; }
.queue-row:last-child { border-bottom: none; }
.queue-name { flex: 1; color: #1f2937; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.queue-size { color: #6b7280; font-size: 0.75rem; }
.queue-done { background: #ecfdf5; }
.queue-error { background: #fef2f2; }

/* ───────────── Boutons ───────────── */
.btn-icon {
  background: transparent; border: 1px solid transparent;
  border-radius: 6px; padding: 4px 8px;
  font-size: 0.95rem; cursor: pointer; color: #6b7280;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
  display: inline-flex; align-items: center; justify-content: center;
}
.btn-icon--ghost:hover:not(:disabled) {
  background: #f3f4f6; color: #111827;
}
.btn-icon--danger:hover:not(:disabled) {
  background: #fef2f2; color: #b91c1c; border-color: #fecaca;
}
.btn-icon--save { color: #059669; }
.btn-icon--save:hover:not(:disabled) { background: #ecfdf5; }
.btn-icon:disabled { opacity: 0.4; cursor: not-allowed; }

.btn-primary {
  background: linear-gradient(135deg, #6c63ff, #5a52d5); color: white; border: none;
  padding: 7px 14px; border-radius: 6px; font-size: 0.82rem; cursor: pointer;
  font-weight: 500;
  box-shadow: 0 2px 6px rgba(108, 99, 255, 0.25);
  transition: filter 0.15s, box-shadow 0.15s;
}
.btn-primary:hover:not(:disabled) { filter: brightness(1.06); box-shadow: 0 3px 10px rgba(108, 99, 255, 0.35); }
.btn-primary:disabled { opacity: 0.55; cursor: not-allowed; box-shadow: none; }

.btn-secondary {
  background: #fff; color: #374151; border: 1px solid #d1d5db;
  padding: 7px 14px; border-radius: 6px; font-size: 0.82rem; cursor: pointer;
}
.btn-secondary:hover:not(:disabled) { background: #f9fafb; }
.btn-secondary-sm {
  background: #fff; color: #374151; border: 1px solid #d1d5db;
  padding: 4px 10px; border-radius: 6px; font-size: 0.78rem; cursor: pointer;
}
.btn-secondary-sm:hover:not(:disabled) { background: #f9fafb; }

.btn-danger {
  background: #b91c1c; color: white; border: none;
  padding: 7px 14px; border-radius: 6px; font-size: 0.82rem; cursor: pointer;
  font-weight: 500;
}
.btn-danger:hover:not(:disabled) { background: #991b1b; }
.btn-danger-sm {
  background: #b91c1c; color: white; border: none;
  padding: 4px 10px; border-radius: 6px; font-size: 0.78rem; cursor: pointer;
}
.btn-danger-sm:hover:not(:disabled) { background: #991b1b; }
.btn-ghost-sm {
  background: transparent; color: #6b7280; border: none;
  padding: 4px 8px; border-radius: 6px; font-size: 0.85rem; cursor: pointer;
}
.btn-ghost-sm:hover:not(:disabled) { background: #f3f4f6; color: #111827; }
.btn-danger:disabled,
.btn-danger-sm:disabled,
.btn-secondary-sm:disabled,
.btn-secondary:disabled { opacity: 0.55; cursor: not-allowed; }

/* ───────────── Actions du détail ───────────── */
.detail-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 22px;
}
.action-card {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  padding: 10px 16px;
  background: #fff;
  border: 1px solid #e0e4e8;
  border-radius: 10px;
  font-size: 0.87rem;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s, background 0.15s, transform 0.08s;
}
.action-card:hover:not(:disabled) {
  border-color: #6c63ff;
  box-shadow: 0 3px 10px rgba(108, 99, 255, 0.12);
  transform: translateY(-1px);
}
.action-card:disabled { opacity: 0.5; cursor: not-allowed; }
.action-card--open { border-color: #6c63ff; background: #f8f7ff; color: #5a52d5; }
.action-card-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #6c63ff;
}
.action-card--accent {
  background: linear-gradient(135deg, #6c63ff, #5a52d5);
  border-color: transparent;
  color: #fff;
  box-shadow: 0 2px 6px rgba(108, 99, 255, 0.25);
}
.action-card--accent .action-card-icon { color: #fff; }
.action-card--accent:hover:not(:disabled) {
  border-color: transparent;
  filter: brightness(1.06);
  box-shadow: 0 3px 12px rgba(108, 99, 255, 0.35);
}
.action-card--danger { color: #b91c1c; }
.action-card--danger .action-card-icon { color: #b91c1c; }
.action-card--danger:hover:not(:disabled) {
  border-color: #fca5a5;
  background: #fef2f2;
  box-shadow: 0 3px 10px rgba(185, 28, 28, 0.10);
}
.action-card--confirm {
  cursor: default;
  gap: 10px;
  color: #991b1b;
  border-color: #fecaca;
  background: #fef2f2;
}
.action-card--confirm:hover { transform: none; box-shadow: none; border-color: #fecaca; }

/* ───────────── Arborescence documents ───────────── */
.fs-tree {
  list-style: none;
  padding: 0;
  margin: 0;
  border: 1px solid #e0e4e8;
  border-radius: 10px;
  overflow: hidden;
}
.fs-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-bottom: 1px solid #f3f4f6;
  font-size: 0.88rem;
  transition: background 0.1s;
}
.fs-row:last-child { border-bottom: none; }
.fs-row:hover:not(.fs-row--folder) { background: #fafafa; }
.fs-row--folder { padding-top: 0; padding-bottom: 0; background: #fbfaff; }
.fs-folder-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 9px 0;
  background: none;
  border: none;
  font: inherit;
  color: #111827;
  cursor: pointer;
  text-align: left;
}
.fs-chevron {
  display: inline-flex;
  color: #9ca3af;
  transition: transform 0.15s;
  flex-shrink: 0;
}
.fs-chevron--open { transform: rotate(90deg); color: #6c63ff; }
.fs-folder-btn:hover .fs-chevron { color: #6c63ff; }
.fs-check {
  flex-shrink: 0;
  margin: 0;
  cursor: pointer;
  accent-color: #6c63ff;
}
.fs-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #111827;
}
.fs-name--folder { font-weight: 600; }
.fs-count {
  margin-left: auto;
  font-size: 0.72rem;
  color: #9ca3af;
  flex-shrink: 0;
}
.fs-file-main {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}
.fs-meta {
  color: #6b7280;
  font-size: 0.78rem;
  flex-shrink: 0;
  min-width: 62px;
  text-align: right;
}
.fs-actions {
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;
  min-width: 96px;
}
.doc-path { color: #9ca3af; }

/* ───────────── Barre de recherche sticky ───────────── */
.scope-bar {
  position: sticky;
  bottom: 0;
  z-index: 4;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
  margin: 18px -36px -32px;
  padding: 12px 36px;
  background: #fff;
  border-top: 1px solid #e0e4e8;
  box-shadow: 0 -6px 16px rgba(17, 24, 39, 0.05);
}
.scope-bar-info {
  font-size: 0.84rem;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.scope-bar-info strong { color: #111827; font-weight: 600; }
.scope-clear { font-size: 0.8rem; }
.scope-search-btn {
  flex-shrink: 0;
  padding: 10px 22px;
  background: linear-gradient(135deg, #6c63ff, #5a52d5);
  color: #fff;
  border: none;
  border-radius: 999px;
  font-size: 0.87rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(108, 99, 255, 0.3);
  transition: filter 0.15s, box-shadow 0.15s, transform 0.1s;
}
.scope-search-btn:hover:not(:disabled) {
  filter: brightness(1.06);
  box-shadow: 0 4px 12px rgba(108, 99, 255, 0.4);
  transform: translateY(-1px);
}
.scope-search-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  box-shadow: none;
}
@media (max-width: 720px) {
  .scope-bar { margin: 18px -18px -20px; padding: 10px 18px; }
}

/* ───────────── Misc ───────────── */
.error-msg { color: #b91c1c; font-size: 0.85rem; margin: 6px 0 12px 0; }

/* ───────────── Responsive ───────────── */
@media (max-width: 720px) {
  .explorer-overlay { padding: 0; }
  .explorer-shell {
    border-radius: 0;
    max-height: 100vh;
    padding: 20px 18px;
  }
  .fs-row { flex-wrap: wrap; row-gap: 6px; }
  .fs-file-main { flex-basis: 100%; }
  .fs-meta { min-width: auto; }
  .fs-actions { min-width: auto; margin-left: auto; }
  .detail-actions .action-card { flex: 1; justify-content: center; }
  .ws-grid { grid-template-columns: 1fr; }
}
</style>
